/* eslint-disable no-param-reassign -- Redux Toolkit uses Immer */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type FieldElement, type StyleData } from '../../../shared/types';

export interface PreviewFormData {
  name: string;
  urlSlug?: string;
  customFormUid?: string;
  formData: {
    ui: FieldElement[];
    dataProviders: unknown;
  };
  styleData?: StyleData;
  logo?: string;
}

export interface FormManagementListRow {
  id: string;
  name: string;
  description: string;
  createdBy?: string;
  createdOn?: string;
  updatedBy?: string;
  updatedOn?: string;
  urlSlug: string;
  customFormUid: string;
  status: string;
  logo?: string;
}

// Re-exported from shared types for backward compatibility
export type { ShareOption } from '../../../shared/types/types';

// Keeping the exact key "management" as requested
export interface ManagementState {
  alerts: {
    isVisible: boolean;
    bannerType: string | null;
    bannerMessage: string | null;
  };
  modal: {
    isOpen: boolean;
    actionType: string | null;
    formData: FormManagementListRow | null;
  };
  preview: {
    isOpen: boolean;
    data: PreviewFormData | null;
  };
  share: {
    isOpen: boolean;
  };
  // When this increments, anything watching it can refetch.
  refetchNonce: number;
  // If you still want a placeholder array like in your base:
  items: string[];
}

const initialState: ManagementState = {
  alerts: {
    isVisible: false,
    bannerType: null,
    bannerMessage: null,
  },
  modal: {
    isOpen: false,
    actionType: null,
    formData: null,
  },
  preview: {
    isOpen: false,
    data: null,
  },
  share: {
    isOpen: false,
  },
  refetchNonce: 0,
  items: [],
};

const managementSlice = createSlice({
  name: 'management',
  initialState,
  reducers: {
    // ===== Alerts =====
    showAlert(
      state,
      action: PayloadAction<{
        bannerType: string;
        bannerMessage: string | null;
      }>
    ) {
      state.alerts.isVisible = true;
      state.alerts.bannerType = action.payload.bannerType;
      state.alerts.bannerMessage = action.payload.bannerMessage;
    },
    hideAlert(state) {
      state.alerts.isVisible = false;
      state.alerts.bannerType = null;
      state.alerts.bannerMessage = null;
    },

    // ===== Form Action Modal =====
    openFormActionModal(
      state,
      action: PayloadAction<{
        actionType: string;
        formData: FormManagementListRow | null;
      }>
    ) {
      state.modal.isOpen = true;
      state.modal.actionType = action.payload.actionType;
      state.modal.formData = action.payload.formData;
    },
    closeFormActionModal(state) {
      state.modal.isOpen = false;
      state.modal.actionType = null;
      state.modal.formData = null;
    },
    openShareModal(state) {
      state.share.isOpen = true;
    },
    closeShareModal(state) {
      state.share.isOpen = false;
    },

    // ===== Preview Modal + Data =====
    openPreviewModal(state) {
      state.preview.isOpen = true;
    },
    closePreviewModal(state) {
      state.preview.isOpen = false;
    },
    setPreviewFormData(state, action: PayloadAction<PreviewFormData | null>) {
      state.preview.data = action.payload;
    },

    // ===== Refetch trigger =====
    triggerFormsRefetch(state) {
      state.refetchNonce += 1;
    },
  },
});

// Unified actions export for this slice
export const managementActions = managementSlice.actions;

// Reducer exports (both default and named for convenience)
export const managementReducer = managementSlice.reducer;
export default managementSlice.reducer;
